import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import "./InvoiceFormNote.css";
import { ConcurrentEditError } from "../../../../Services/InvoiceService/InvoiceService";

export interface NoteHistoryEntry {
  action: "added" | "edited" | "cleared";
  userName: string;
  timestamp: string;
}

export interface NoteData {
  note: string;
  rowVersion: string;
}

export interface InvoiceFormNoteProps {
  itemId: string;
  currentUserName: string;
  onLoad: (itemId: string) => Promise<NoteData>;
  onSave: (itemId: string, note: string, rowVersion: string) => Promise<NoteData>;
  onHistoryEntry?: (entry: NoteHistoryEntry) => void;
  onEditedStateChange?: (isEdited: boolean) => void;
  maxLength?: number;
  /** When true, the tab is kept mounted but visually hidden so it can
   * fetch/refresh its data even before the user clicks into it. */
  hidden?: boolean;
}

export interface InvoiceFormNoteHandle {
  hasUnsavedChanges: () => boolean;
  guardNavigation: (onLeave: () => void) => void;
}

const InvoiceFormNote = forwardRef<InvoiceFormNoteHandle, InvoiceFormNoteProps>(
  (
    {
      itemId,
      currentUserName,
      onLoad,
      onSave,
      onHistoryEntry,
      onEditedStateChange,
      maxLength = 1000,
      hidden = false,
    },
    ref
  ) => {
    const [savedNote, setSavedNote] = useState("");
    const [draft, setDraft] = useState("");
    const [rowVersion, setRowVersion] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [conflictMessage, setConflictMessage] = useState<string | null>(null);
    const [showLeaveWarning, setShowLeaveWarning] = useState(false);

    const pendingLeaveAction = useRef<(() => void) | null>(null);

    const load = useCallback(async () => {
      if (!itemId) return;

      setError(null);
      setConflictMessage(null);
      setIsLoading(true);
      try {
        const fetched = await onLoad(itemId);
        setSavedNote(fetched?.note ?? "");
        setDraft(fetched?.note ?? "");
        setRowVersion(fetched?.rowVersion ?? "");
        onEditedStateChange?.(!!fetched?.note?.trim());
      } catch (err) {
        setError("Couldn't load the note. Please try again.");
        setSavedNote("");
        setDraft("");
        setRowVersion("");
      } finally {
        setIsLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId]);

    // Fetch as soon as we know which item we're on — not only once the
    // user opens the Note tab. The parent should keep this component
    // mounted (using `hidden`) rather than conditionally rendering it,
    // so this fires on page load and the "Edited" badge / note content
    // are correct immediately.
    useEffect(() => {
      let cancelled = false;
      const run = async () => {
        if (cancelled) return;
        await load();
      };
      run();
      return () => {
        cancelled = true;
      };
    }, [load]);

    const isDirty = draft !== savedNote;
    const isEdited = savedNote.trim().length > 0;

    useEffect(() => {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (!isDirty) return;
        e.preventDefault();
        e.returnValue = "";
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    useImperativeHandle(ref, () => ({
      hasUnsavedChanges: () => isDirty,
      guardNavigation: (onLeave: () => void) => {
        if (!isDirty) {
          onLeave();
          return;
        }
        pendingLeaveAction.current = onLeave;
        setShowLeaveWarning(true);
      },
    }));

    const handleSave = async () => {
      setIsSaving(true);
      setError(null);
      setConflictMessage(null);

      const wasEmpty = savedNote.trim().length === 0;
      const isNowEmpty = draft.trim().length === 0;

      try {
        const result = await onSave(itemId, draft, rowVersion);
        const refreshed = await onLoad(itemId);
        setSavedNote(refreshed.note ?? draft);
        setRowVersion(refreshed.rowVersion ?? "");

        const nowEdited = !isNowEmpty;
        onEditedStateChange?.(nowEdited);

        if (!wasEmpty || !isNowEmpty) {
          let action: NoteHistoryEntry["action"];
          if (wasEmpty && !isNowEmpty) action = "added";
          else if (!wasEmpty && isNowEmpty) action = "cleared";
          else action = "edited";

          onHistoryEntry?.({
            action,
            userName: currentUserName,
            timestamp: new Date().toISOString(),
          });
        }
      } catch (err) {
        if (err instanceof ConcurrentEditError) {
          // Don't touch savedNote/draft here — let the user decide to
          // refresh rather than silently discarding their edit.
          setConflictMessage(err.message);
        } else {
          setError("Couldn't save the note. Please try again.");
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handleRefresh = async () => {
      await load();
    };

    const handleStay = () => {
      setShowLeaveWarning(false);
      pendingLeaveAction.current = null;
    };

    const handleLeave = () => {
      setShowLeaveWarning(false);
      setDraft(savedNote);
      const action = pendingLeaveAction.current;
      pendingLeaveAction.current = null;
      action?.();
    };

    return (
      <div
        className="invoice-note-tab"
        style={hidden ? { display: "none" } : undefined}
        aria-hidden={hidden}
      >
        <div className="invoice-note-tab__header">
          <span className="invoice-note-tab__label">Note</span>
          {isEdited && <span className="invoice-note-tab__badge">Edited</span>}
        </div>

        <textarea
          className="invoice-note-tab__textarea"
          placeholder={isLoading ? "Loading note…" : "Add a note about this item"}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
          maxLength={maxLength}
          disabled={isSaving || isLoading || !!conflictMessage}
          aria-label="Invoice item note"
        />

        {error && <p className="invoice-note-tab__error">{error}</p>}

        {conflictMessage && (
          <div className="invoice-note-tab__conflict" role="alert">
            <p className="invoice-note-tab__conflict-text" style={{ color: "red" }}>{conflictMessage}</p>
            {/* <button
              type="button"
              className="invoice-note-tab__refresh-button"
              onClick={handleRefresh}
            >
              Refresh
            </button> */}
          </div>
        )}

        <div className="invoice-note-tab__footer">
          <span
            className={
              "invoice-note-tab__char-count" +
              (draft.length >= maxLength
                ? " invoice-note-tab__char-count--limit"
                : "")
            }
          >
            {draft.length}/{maxLength}
          </span>

          <button
            type="button"
            className="invoice-note-tab__save-button"
            onClick={handleSave}
            disabled={!isDirty || isSaving || isLoading || !!conflictMessage}
          >
            {isSaving ? "Saving…" : isLoading ? "Loading…" : "Save"}
          </button>
        </div>

        {showLeaveWarning && (
          <div className="invoice-note-tab__overlay" role="presentation">
            <div
              className="invoice-note-tab__modal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="unsaved-changes-title"
            >
              <p id="unsaved-changes-title" className="invoice-note-tab__modal-text">
                You have unsaved changes. Are you sure you want to leave?
              </p>
              <div className="invoice-note-tab__modal-actions">
                <button type="button" className="invoice-note-tab__modal-stay" onClick={handleStay}>
                  Stay
                </button>
                <button type="button" className="invoice-note-tab__modal-leave" onClick={handleLeave}>
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

InvoiceFormNote.displayName = "InvoiceFormNote";

export default InvoiceFormNote;